import React, { useRef, useState } from "react";
import styled from "styled-components";
import { subscribeNewsletter } from "../../lib/functions";

const Wrap = styled.section`
  padding: 96px 0 128px;
  background: var(--nd-black);
  color: var(--nd-text-primary);
  min-height: 60vh;
`;

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 24px;
`;

const Heading = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 40px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--nd-text-display);
  margin: 0 0 12px;
  line-height: 1.1;

  @media (max-width: 600px) { font-size: 32px; }
`;

const Sub = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: var(--nd-text-secondary);
  margin: 0 0 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  background: var(--nd-black);
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  padding: 10px 12px;
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  &:focus { outline: none; border-color: var(--nd-text-display); }
`;

const ConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  color: var(--nd-text-secondary);
  margin-top: 8px;
  cursor: pointer;
  line-height: 1.5;

  & input { margin-top: 4px; flex-shrink: 0; }
`;

const Submit = styled.button`
  margin-top: 16px;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: var(--nd-text-display);
  color: var(--nd-black);
  border: 0;
  border-radius: 999px;
  padding: 12px 24px;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 200ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const Status = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${({ kind }) =>
    kind === "error" ? "var(--nd-accent)"
    : kind === "success" ? "var(--nd-success)"
    : "var(--nd-text-secondary)"};
  margin: 12px 0 0;
`;

const NewsletterForm = ({ compact = false }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ kind: null, msg: null });
  const nameRef = useRef(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setStatus({ kind: "error", msg: "Name required" });
      return;
    }
    if (!email.trim()) {
      setStatus({ kind: "error", msg: "Email required" });
      return;
    }
    if (!consent) {
      setStatus({ kind: "error", msg: "Please tick the consent box" });
      return;
    }
    setStatus({ kind: "info", msg: "Subscribing…" });
    try {
      const res = await subscribeNewsletter({
        name: name.trim(),
        email: email.trim(),
        consent: true,
      });
      if (res && res.alreadySubscribed) {
        setStatus({ kind: "success", msg: "You're already on the list. We re-confirmed your address." });
      } else {
        setStatus({ kind: "success", msg: "You're in. A welcome email is on its way." });
      }
      setName("");
      setEmail("");
      setConsent(false);
      if (nameRef.current) nameRef.current.blur();
    } catch (err) {
      setStatus({
        kind: "error",
        msg: (err && err.message) || "Subscribe failed. Try again in a moment.",
      });
    }
  };

  if (compact) {
    return (
      <Form onSubmit={onSubmit} aria-label="Newsletter signup">
        <Field>
          <Label htmlFor="newsletter-mini-name">Name</Label>
          <Input
            id="newsletter-mini-name"
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="your name"
          />
        </Field>
        <Field>
          <Label htmlFor="newsletter-mini-email">Email</Label>
          <Input
            id="newsletter-mini-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
            placeholder="you@company.com"
          />
        </Field>
        <ConsentRow>
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>Send me the newsletter and the occasional product update.</span>
        </ConsentRow>
        <Submit type="submit" disabled={status.kind === "info"}>
          {status.kind === "info" ? "Subscribing…" : "Subscribe"}
        </Submit>
        {status.msg && <Status kind={status.kind}>{status.msg}</Status>}
      </Form>
    );
  }

  return (
    <Wrap>
      <Container>
        <Kicker>NEWSLETTER</Kicker>
        <Heading>What we learned this month.</Heading>
        <Sub>
          One short email, once a month. New labs, what broke in production,
          and what we'd build differently. No pitches, no churn-bait. Reply
          with "unsubscribe" any time and you're out.
        </Sub>
        <Form onSubmit={onSubmit} aria-label="Newsletter signup">
          <Field>
            <Label htmlFor="newsletter-name">Name</Label>
            <Input
              id="newsletter-name"
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoComplete="name"
              placeholder="Your name"
            />
          </Field>
          <Field>
            <Label htmlFor="newsletter-email">Email</Label>
            <Input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </Field>
          <ConsentRow>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>
              Send me the Dioscuro newsletter and the occasional product
              update. I can unsubscribe at any time by replying with
              "unsubscribe".
            </span>
          </ConsentRow>
          <Submit type="submit" disabled={status.kind === "info"}>
            {status.kind === "info" ? "Subscribing…" : "Subscribe"}
          </Submit>
          {status.msg && <Status kind={status.kind}>{status.msg}</Status>}
        </Form>
      </Container>
    </Wrap>
  );
};

export default NewsletterForm;
